import Link from "next/link";
import Image from "next/image";
import { ReactChild, VFC } from "react";

import { Bars } from "react-loader-spinner";

import { IndexArticle } from "lib/type";
import { IconType } from "react-icons";

import { FaTags, FaThumbsUp } from "react-icons/fa";
import { FiClock } from "react-icons/fi";

type ArticleCardProps = {
  article: IndexArticle;
};

export const ArticleCard: VFC<ArticleCardProps> = ({ article }) => {
  if (!article) return null;
  const { articleID, thumb, color, title, author, createdAt, tags, likeCount } =
    article;

  const tagList = tags?.map((tag: string, index: number) => {
    return (
      <li key={index} className="text-sm leading-none">
        <Link href={`/tags/${tag}`} prefetch={false}>
          <a className="whitespace-nowrap">#{tag}</a>
        </Link>
      </li>
    );
  });

  return (
    <li
      key={articleID}
      className="shadow-light-700 dark:shadow-dark-400 border-l-[0.5rem] border-solid shadow-lg"
      style={{ borderColor: color }}
    >
      <div
        className="before:bg-light-600 dark:before:bg-dark-600 relative z-10 flex h-full
                  flex-col overflow-hidden before:absolute before:top-0 before:-right-4
                  before:h-full before:w-1/2 before:-skew-x-12"
      >
        <Link href={`/articles/${articleID}`} prefetch={false}>
          <a className="absolute top-1/2 -right-2 -translate-y-1/2 rotate-12 text-9xl opacity-20 hover:opacity-20">
            {thumb}
          </a>
        </Link>
        <Link href={`/articles/${articleID}`} prefetch={false}>
          <a className="relative z-10 flex-1 p-4">
            <h3 className="mb-2 text-xl">{title}</h3>
            <p className="flex items-center space-x-2">
              <span className="flex items-center">
                <FiClock className="mr-1" />
                {createdAt}
              </span>
              <span className="flex items-center">
                <FaThumbsUp className="mr-1 text-lg" />
                {likeCount && likeCount < 0 ? 0 : likeCount}
              </span>
            </p>
          </a>
        </Link>
        {tags && tags.length > 0 && (
          <ul className="relative z-10 flex w-[fit-content] flex-wrap items-center gap-y-1 gap-x-2 px-4 pb-2">
            <li>
              <FaTags />
            </li>
            {tagList}
          </ul>
        )}
        {author && (
          <Link href={`/users/${author?.uid}`} prefetch={false}>
            <a className="relative z-10 mt-2 flex w-[fit-content] items-center px-4 pb-4">
              <div className="nextimg mr-1 h-8 w-8">
                <Image
                  src={author?.avatar || "/default.svg"}
                  alt={author?.name || ""}
                  width="50"
                  height="50"
                  className="rounded-full object-cover"
                />
              </div>
              <p>{author?.name}</p>
            </a>
          </Link>
        )}
      </div>
    </li>
  );
};

type BtnProps = {
  children: ReactChild;
  Icon?: IconType;
};
export const Btn: VFC<BtnProps> = ({ children, Icon }) => {
  return (
    <div
      className="border-dark-600 dark:border-light-400 from-light-400 to-light-700 dark:from-dark-400 dark:to-dark-700 text-light dark:text-dark shadow-light-800 dark:shadow-dark-300
        mx-auto flex w-[fit-content] items-center rounded-lg border-2 border-solid bg-gradient-to-r p-[0.1rem] font-bold leading-none shadow-lg"
    >
      <div className="bg-gradient-radial from-dark-400 to-dark-900 dark:from-light-50 dark:to-light-700 relative overflow-hidden px-7 py-3">
        {Icon && (
          <Icon className="absolute top-1/2 right-0 -translate-y-1/2 rotate-6 transform text-4xl opacity-40" />
        )}
        {children}
      </div>
    </div>
  );
};

export const Loading: VFC<{ color?: string }> = ({ color = "#00cce7" }) => {
  return (
    <div className="fixed inset-0 z-30 h-screen w-screen backdrop-blur-sm">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <Bars color={color} height={100} width={100} />
      </div>
    </div>
  );
};

export const MiniLoader = () => {
  return (
    <div className="p-4 lg:col-span-9 lg:px-0">
      <Bars color="#00cce7" height={100} width={100} />
    </div>
  );
};

export const BigLoader = () => {
  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform">
      <Bars color="#00cce7" height={100} width={100} />
    </div>
  );
};

export const Logo = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 512 106.87"
      className="dark:fill-current"
    >
      <path d="M0 104.68v-97.2h36.1q13 .12 20.94 5.8t7.96 19.59a27.59 27.59 0 0 1-2.41 11.73 17.22 17.22 0 0 1-8.32 8.13v.13q10.44 1.41 15.15 8t4.71 16.83q0 15.21-10.9 21.07a50.42 50.42 0 0 1-24.3 5.87zm25.27-59h2.83a.48.48 0 0 0 .33.12h.45a24.52 24.52 0 0 0 8.83-1.8c2.7-1.12 4.06-3.65 4.06-7.61s-1.29-6.57-3.87-7.7a22.15 22.15 0 0 0-8.38-1.84h-4.25zm0 39.7h3.22a.41.41 0 0 0 .32.13h3.68c1.11 0 2.23 0 3.35-.13a22.86 22.86 0 0 0 8.76-2.46c2.67-1.39 4-4 4-7.79q0-6.61-4.38-8.56a24.7 24.7 0 0 0-9.54-2.07h-9.41zM114 36.35a45 45 0 0 1 27.91 9.35q11.92 9.22 11.92 26.11t-11.96 25.91a44.56 44.56 0 0 1-27.87 9.15 44.84 44.84 0 0 1-27.85-9.09q-12-9.09-12-26t11.9-26.08a44.35 44.35 0 0 1 27.95-9.22zm-11.13 24.95a15.22 15.22 0 0 0-4 10.51 14.86 14.86 0 0 0 4 10.57q4 4.25 11.09 4.25t11.15-4.27a15.1 15.1 0 0 0 3.89-10.62 15.45 15.45 0 0 0-3.93-10.55q-3.93-4.47-11.15-4.47t-11.05 4.58zM180.22 4a12.65 12.65 0 0 1 3.87 9.28 12.65 12.65 0 0 1-3.87 9.28 13.07 13.07 0 0 1-18.57 0 12.68 12.68 0 0 1-3.86-9.28 12.85 12.85 0 0 1 3.86-9.37 12.6 12.6 0 0 1 9.29-3.91 13.14 13.14 0 0 1 9.28 4zm2.45 100.68h-23.46v-66.13h23.46zM221.21 104.68h-23.46v-46.8h-7.61v-19.33h7.61v-19.86h23.46v19.86h13.28v19.33h-13.28zM275.45 36.35a45 45 0 0 1 27.91 9.35q11.92 9.22 11.92 26.11t-11.92 25.91a47.17 47.17 0 0 1-55.76.06q-12-9.09-12-26t11.94-26.08a44.35 44.35 0 0 1 27.91-9.22zm-11.09 24.95a15.22 15.22 0 0 0-4 10.51 14.87 14.87 0 0 0 4 10.57q4 4.25 11.09 4.25t11.15-4.27a15.1 15.1 0 0 0 3.93-10.62 15.45 15.45 0 0 0-3.93-10.55q-3.93-4.47-11.15-4.47t-11.09 4.58zM339 49.37h.26a21.16 21.16 0 0 1 9-8.76 27.17 27.17 0 0 1 12.44-2.84l4.51-.13v22.56a3.56 3.56 0 0 1-1-.38 4 4 0 0 0-1-.39 15.86 15.86 0 0 0-3.87-1 31.66 31.66 0 0 0-4.13-.26q-9.8 0-13 5.74t-3.21 14.21v26.56h-23.46v-66.13h23.46zM436.72 73.09v2.45h-49.38q0 7.23 3.74 11t11 3.8a14.07 14.07 0 0 0 6.47-1.34 11.8 11.8 0 0 0 4.7-4.64h22.56q-2.81 11.74-12.57 17.13a42.92 42.92 0 0 1-21.07 5.38q-16.38 0-27.33-9t-11-26.1q0-16.5 10.25-25.92t26.36-9.41q17.28 0 26.75 9.93t9.48 26.81zm-21.79-11.09a11.82 11.82 0 0 0-4.71-7.48 14 14 0 0 0-8.57-2.84 15.66 15.66 0 0 0-9 2.62 11.63 11.63 0 0 0-4.9 7.7zM512 104.68h-23.46v-7.35h-.26a17.51 17.51 0 0 1-7.73 6.9 27.42 27.42 0 0 1-10.19 2.51 4 4 0 0 1-1 .13h-1q-14.7 0-24-10.51a35.89 35.89 0 0 1-9.35-24.68 36.22 36.22 0 0 1 9.12-24.68q9.21-10.5 23.91-10.51h1.29a7.05 7.05 0 0 1 1.29.13 30.08 30.08 0 0 1 9.86 2.52 21.6 21.6 0 0 1 8.06 6v-6.59h23.46zm-48.86-43.38a15.22 15.22 0 0 0-4 10.51 14.87 14.87 0 0 0 4 10.57q4 4.25 11.09 4.25t11.15-4.27a15.1 15.1 0 0 0 3.93-10.62 15.45 15.45 0 0 0-3.93-10.55q-3.93-4.47-11.15-4.47t-11.09 4.58z" />
    </svg>
  );
};
